/** Shown when camera switch fails or would break the call (e.g. remote video freeze). */
export const SWITCH_CAMERA_NOT_SUPPORTED_MSG =
  'Switch camera is not supported on this device.';

/**
 * Enumerate video inputs: distinct non-empty deviceIds + raw count (some OSes expose 2 cameras with empty ids).
 */
export async function getVideoDevicesSummary() {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
    return { ids: [], videoInputCount: 0 };
  }
  const devices = await navigator.mediaDevices.enumerateDevices();
  const inputs = devices.filter((d) => d.kind === 'videoinput');
  const ids = [...new Set(inputs.map((d) => d.deviceId).filter(Boolean))];
  return { ids, videoInputCount: inputs.length };
}

export async function getVideoInputDeviceIds() {
  const { ids } = await getVideoDevicesSummary();
  return ids;
}

/** @deprecated use shouldOfferCameraSwitch for UI */
export function hasMultipleVideoInputs(deviceIds) {
  return Array.isArray(deviceIds) && deviceIds.filter(Boolean).length >= 2;
}

/**
 * Show camera switch only when multiple video inputs are enumerated (same rule on mobile and desktop).
 * - Two+ distinct non-empty device ids, or
 * - Two+ videoinput entries (e.g. blank ids on some Android builds).
 */
export function shouldOfferCameraSwitch({ ids, videoInputCount }) {
  const distinct = (ids || []).filter(Boolean);
  if (distinct.length >= 2) {
    return true;
  }
  if (videoInputCount >= 2) {
    return true;
  }
  return false;
}

function gumVideo(constraints) {
  return navigator.mediaDevices.getUserMedia({
    video: constraints,
    audio: false,
  });
}

function snapshotVideoTrack(track) {
  if (!track || track.readyState === 'ended') {
    return { deviceId: null, facingMode: null, trackId: null };
  }
  const s = track.getSettings?.() || {};
  return {
    deviceId: s.deviceId || null,
    facingMode: s.facingMode || null,
    trackId: track.id || null,
  };
}

/**
 * True if the new track is plausibly a different camera than before switching.
 * Browsers often satisfy deviceId:{ideal: otherId} with the *current* camera — we reject that.
 */
function isMeaningfulCameraChange(before, nextTrack) {
  if (!nextTrack) {
    return false;
  }
  if (!before.deviceId && !before.facingMode && !before.trackId) {
    return true;
  }
  const s = nextTrack.getSettings?.() || {};
  const d = s.deviceId || null;
  const f = s.facingMode || null;
  if (before.deviceId && d && before.deviceId !== d) {
    return true;
  }
  if (before.facingMode && f && before.facingMode !== f) {
    return true;
  }
  if (before.trackId && nextTrack.id && before.trackId !== nextTrack.id) {
    return true;
  }
  if (before.deviceId && d && before.deviceId === d) {
    return false;
  }
  if (before.facingMode && f && before.facingMode === f) {
    return false;
  }
  return true;
}

/** Which facingMode values to try when switching. Many browsers omit `facingMode` in track settings. */
function facingModesToTry(facingBase) {
  if (facingBase === 'user') return ['environment'];
  if (facingBase === 'environment') return ['user'];
  return ['environment', 'user'];
}

/**
 * Build ordered getUserMedia attempts for switching camera.
 * Prefer exact deviceId before ideal — ideal often keeps the active camera.
 * @param {{ targetDeviceId?: string|null, facingBase?: string|null }} opts
 */
function buildCameraSwitchAttempts({ targetDeviceId, facingBase }) {
  const supported = navigator.mediaDevices?.getSupportedConstraints?.() || {};
  const attempts = [];

  if (targetDeviceId) {
    attempts.push(() => gumVideo({ deviceId: { exact: targetDeviceId } }));
    attempts.push(() => gumVideo({ deviceId: { ideal: targetDeviceId } }));
  }

  for (const fm of facingModesToTry(facingBase)) {
    if (supported.facingMode) {
      attempts.push(() => gumVideo({ facingMode: { ideal: fm } }));
      attempts.push(() => gumVideo({ facingMode: { exact: fm } }));
    }
    attempts.push(() => gumVideo({ facingMode: fm }));
  }

  return attempts;
}

async function runAttempts(attempts, baselineBefore) {
  let lastErr;
  for (const fn of attempts) {
    try {
      const stream = await fn();
      const t = stream.getVideoTracks()[0];
      if (!t) {
        continue;
      }
      if (isMeaningfulCameraChange(baselineBefore, t)) {
        return stream;
      }
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('getUserMediaForCameraSwitch failed');
}

/**
 * Cross-browser camera switch using only standard APIs (`getUserMedia`, `deviceId`, `facingMode`).
 * No Chrome-only branches: Safari, Firefox, Edge, and in-app WebViews run the same fallbacks.
 *
 * Many engines refuse a second camera while the first video track is still active; when `mediaStream`
 * is passed, a failed first pass removes/stops local video tracks, waits briefly, then retries.
 *
 * @param {{
 *   targetDeviceId?: string|null,
 *   currentVideoTrack?: MediaStreamTrack|null,
 *   priorFacingMode?: string|null,
 *   mediaStream?: MediaStream|null,
 * }} opts
 * @returns {Promise<MediaStream>}
 */
export async function getUserMediaForCameraSwitch({
  targetDeviceId,
  currentVideoTrack,
  priorFacingMode,
  mediaStream,
}) {
  const facingBase =
    currentVideoTrack?.getSettings?.()?.facingMode ?? priorFacingMode ?? null;
  const baselineBefore = snapshotVideoTrack(currentVideoTrack);

  const attemptsPass = buildCameraSwitchAttempts({
    targetDeviceId: targetDeviceId || null,
    facingBase,
  });

  try {
    return await runAttempts(attemptsPass, baselineBefore);
  } catch (firstErr) {
    if (!mediaStream) {
      throw firstErr;
    }
    const errName = firstErr && firstErr.name;
    if (errName === 'NotAllowedError' || errName === 'PermissionDeniedError') {
      throw firstErr;
    }
    const videoTracks = [...mediaStream.getVideoTracks()];
    if (videoTracks.length === 0) {
      throw firstErr;
    }
    videoTracks.forEach((t) => {
      try {
        mediaStream.removeTrack(t);
      } catch {
        /* ignore */
      }
      t.stop();
    });
    await new Promise((resolve) => setTimeout(resolve, 220));

    const retryBaseline = snapshotVideoTrack(null);
    const retryAttempts = buildCameraSwitchAttempts({
      targetDeviceId: targetDeviceId || null,
      facingBase: priorFacingMode ?? facingBase,
    });
    return await runAttempts(retryAttempts, retryBaseline);
  }
}
